import { type UserInfo } from '@/api/generated/model';
import { isSessionTokenValid } from '@/features/auth/authSession';
import { jwtDecode } from 'jwt-decode';
import {
  googleLogin,
  sendMagicLink,
  verifyMagicLink,
} from '../generated/authentication/authentication';

export interface GoogleUserInfo {
  sub: string;
  email: string;
  email_verified: boolean;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  locale: string;
  iss: string;
  aud: string;
  iat: number;
  exp: number;
  azp: string;
}

const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_INFO: 'user_info',
  USER_ID: 'user_id',
} as const;

function notifyAuthChange() {
  window.dispatchEvent(new Event('fintecy-auth-change'));
}

export const authService = {
  async handleGoogleLogin(credential: string) {
    try {
      const decodedToken = jwtDecode<GoogleUserInfo>(credential);

      const userInfo: UserInfo = {
        googleSub: decodedToken.sub,
        email: decodedToken.email,
        emailVerified: decodedToken.email_verified,
        name: decodedToken.name,
        firstName: decodedToken.given_name,
        lastName: decodedToken.family_name,
        profilePicture: decodedToken.picture,
        locale: decodedToken.locale,
        provider: 'google',
        lastLogin: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));

      // Backend reads the Google ID token from Authorization: Bearer (see openapi /api/auth/google)
      this.setToken(credential);
      const userId = await googleLogin();

      if (userId) {
        this.setUserId(userId);
      }

      return userId;
    } catch (error) {
      console.error('Google authentication error:', error);
      this.removeToken();
      throw error;
    }
  },

  async sendMagicLink(email: string) {
    return sendMagicLink({ email });
  },

  async verifyMagicLink(email: string, code: string) {
    const response = await verifyMagicLink({ email, code });

    if (response.token) {
      this.setToken(response.token);
    }
    if (response.userId) {
      this.setUserId(response.userId);
    }

    return response;
  },

  setToken(token: string) {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    notifyAuthChange();
  },

  getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  removeToken() {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_ID);
    notifyAuthChange();
  },

  setUserId(userId: string) {
    localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
  },

  getUserId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.USER_ID);
  },

  removeUserInfo() {
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  },

  isAuthenticated(): boolean {
    return isSessionTokenValid(this.getToken());
  },
};
